// File: src/components/ReadlaterCarousel.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import "../styles/ReadLater_carousel.css";
import bigLeftArrow from '../images/bigLeftArrow.png';
import bigRightArrow from '../images/bigRightArrow.png';
import emptyStateIcon from "../images/emptyStateIcon.png";
import { fetchReadLaterBooks } from "../services/AllServices";
import { speakText } from './utils/speechUtils.js';

const ReadlaterCarousel = () => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [readLaterBooks, setReadLaterBooks] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Calculate visibility states
  const maxIndex = Math.max(0, readLaterBooks.length - 7);
  const showArrows = readLaterBooks.length > 7; // Show arrows when more than 7 books
  const isBeginning = currentIdx === 0;
  const isEnd = currentIdx >= maxIndex;

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const response = await fetchReadLaterBooks();
        setReadLaterBooks(response?.data || []);
        setBooks(response?.data || []);
      } catch (error) {
        console.error("Error fetching Read Later books:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBooks();
  }, []);

  const prevSlide = () => {
    setCurrentIdx((prevIdx) => Math.max(0, prevIdx - 1));
  };

  const nextSlide = () => {
    setCurrentIdx((prevIdx) => Math.min(maxIndex, prevIdx + 1));
  };

  if (loading) {
    return (
      <div className="carousel__wrap">
        <div className="carousel__skeleton-loader-lib">
          {[...Array(7)].map((_, index) => (
            <div key={index} className="carousel__skeleton-item"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="carousel__wrap">
      {readLaterBooks.length === 0 ? (
        <div className="empty-state-container">
          <div
            className="genre_heading"
            tabIndex={0}
            onFocus={() =>
              speakText(`Read Later section. You have ${readLaterBooks.length} books for read later.`)
            }
          >
            Read Later
          </div>
          <div className="empty-state">
            <div className="emptyStateIconAndHeading">
              <div className="emptyStateIcon">
                <img src={emptyStateIcon} alt="No books" />
              </div>
              <div className="empty-state__heading">No Books in Read Later</div>
            </div>
            <p className="empty-state__text">
              Add books to your Read Later list to see them here.
            </p>
            <a href="/" className="empty-state__cta">
              Browse Books
            </a>
          </div>
        </div>
      ) : (
        <div className="carousel__inner" role="region" aria-label="Read Later carousel">
          <button
            className={`carousel__btn ${isBeginning ? 'disabled' : ''}`}
            onClick={prevSlide}
            disabled={isBeginning}
            tabIndex={isBeginning ? -1 : 0}
            onFocus={() => speakText("Previous books")}
            aria-label="Previous books"
            type="button"
          >
            <div className="carousel__btn--prev">
              {showArrows && (
                <img src={bigLeftArrow} alt="Previous" className="carousel__btn-arrow" />
              )}
            </div>
          </button>

          <div className="carousel__container">
            <div
              className="genre_heading"
              tabIndex={0}
              onFocus={() =>
                speakText(`Read Later section. You have ${readLaterBooks.length} books for read later.`)
              }
            >
              Read Later
            </div>

            <div
              className="carousel__slide-list"
              style={{ transform: `translateX(-${currentIdx * (10.3 + 2)}vw)` }}
            >
              {readLaterBooks.map((book) => (
                <div className="carousel__slide-item" key={book._id}>
                  {/* Use a button for navigation (semantic, keyboard-accessible) */}
                  <button
                    className="carousel__hover-wrapper"
                    type="button"
                    onClick={() => navigate(`/book-info/${book._id}`, { state: { books } })}
                    onFocus={() => speakText(book.title)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/book-info/${book._id}`, { state: { books } });
                      }
                    }}
                    aria-label={`${book.title}. ${book.author_list?.join(', ') ?? ''}`}
                  >
                    <img
                      src={book.thumbnail}
                      alt={book.title}
                      className="carousel__slide-thumbnail"
                    />
                    <div className="hover_container" aria-hidden="true">
                      <div className="hover_book_name">{book.title}</div>
                      <div className="hover_book_author">{(book.author_list || []).join(", ")}</div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button
            className={`carousel__btn ${isEnd ? 'disabled' : ''}`}
            onClick={nextSlide}
            disabled={isEnd}
            tabIndex={isEnd ? -1 : 0}
            onFocus={() => speakText("Next books")}
            aria-label="Next books"
            type="button"
          >
            <div className="carousel__btn--next">
              {showArrows && (
                <img src={bigRightArrow} alt="Next" className="carousel__btn-arrow" />
              )}
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default ReadlaterCarousel;
